import React, { useState, useCallback } from "react";
import PreloaderLine from "src/components/Preloader/PreloaderLine";
import Container from "@material-ui/core/Container/Container";
import IconButton from "@material-ui/core/IconButton";
import { DeliveryAddress } from "src/types";
import { useCustomerDetailsContext } from "src/providers/CustomerDetailsStore";
import { useAppContext } from "src/providers/AppProvider";
import { columns } from "./DeliveryAddressTable";
import MainTable from "src/components/MainTableWithActions";
import Typography from "@material-ui/core/Typography/Typography";
import { useTranslation } from "react-i18next";
import EditDeliveryAddress from "../../components/EditDeliveryAddress";
import EditIcon from "../../components/icons/EditIcon";
import { theme } from "src/utils/styleTheme/mainStyles";
import DeleteIcon from "../../components/icons/DeleteIcon";
import DeleteAddressConformationDialog from "../../components/DeleteAddressConformationDialog";
import { CustomerDetailsType } from "../../type";
import { useParams } from "react-router-dom";
import { Grid } from "@material-ui/core";

const DeliveryAddressesPage = () => {
  const { customerID } = useParams<CustomerDetailsType>();
  const { t } = useTranslation(`customers`);
  const [deliveryAddressEdit, setDeliveryAddressEdit] = useState<Partial<DeliveryAddress>>({});
  const {
    deliveryAddresses: { deliveryAddresses, loading, currentPage, pageSize, totalCount },
    callAPI: { setContextDeliveryAddressesData, deleteDeliveryAddress, getDeliveryAddresses },
  } = useCustomerDetailsContext();
  const {
    callAppAPI: { toggleModal },
    state: { user },
  } = useAppContext();
  const [deletingAddressId, setDeletingAddressId] = useState<number | null>(null);

  const handleEditDeliveryAddressModal = useCallback((data: Partial<DeliveryAddress>) => {
    toggleModal({ id: "editDeliveryAddressModal", status: true });
    setDeliveryAddressEdit(data);
  }, []);

  const handleDeleteDeliveryAddressModal = (data: Partial<DeliveryAddress>) => {
    setDeletingAddressId(data.key!);
  };

  const isIntegration = user?.partner?.isIntegration;

  let rowActions: ((data: Partial<DeliveryAddress>) => JSX.Element) | undefined;
  if (!isIntegration) {
    rowActions = (data: Partial<DeliveryAddress>) => {
      if (!data.createdByPartner) {
        return <></>;
      }
      return (
        <Grid container>
          <IconButton onClick={() => handleEditDeliveryAddressModal(data)}>
            <EditIcon style={{ width: 16, height: 16 }} />
          </IconButton>
          {!data.isUsed && (
            <IconButton onClick={() => handleDeleteDeliveryAddressModal(data)}>
              <DeleteIcon style={{ width: 16, height: 16 }} />
            </IconButton>
          )}
        </Grid>
      );
    };
  }

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    event?.preventDefault();
    setContextDeliveryAddressesData("currentPage", page);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContextDeliveryAddressesData("pageSize", Number(event.target.value));
  };

  const onAcceptDelete = async () => {
    if (deletingAddressId && customerID) {
      deleteDeliveryAddress(customerID, deletingAddressId).then((status) => {
        status && getDeliveryAddresses(Number(customerID));
        setDeletingAddressId(null);
      });
    }
  };

  const onCancelDelete = () => {
    setDeletingAddressId(null);
  };

  if (loading) {
    return <PreloaderLine />;
  }
  return (
    <Container style={{ margin: 0, padding: 0, paddingBottom: theme.spacing(2) }} maxWidth={"xl"}>
      <Typography variant="h3" paragraph={true} style={{ marginTop: theme.spacing(2) }}>
        {t("labels.details.delivery_addresses")}
      </Typography>
      <PreloaderLine loading={loading} />
      <MainTable<DeliveryAddress>
        data={deliveryAddresses}
        currentPage={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        textAlign="left"
        columnsData={columns}
        rowActions={rowActions}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
      <EditDeliveryAddress data={deliveryAddressEdit} />
      {deletingAddressId && (
        <DeleteAddressConformationDialog
          onAccept={onAcceptDelete}
          onCancel={onCancelDelete}
          subtitle={t("delete_address_confirmation_dialog.sub_title_da")}
        />
      )}
    </Container>
  );
};

export default DeliveryAddressesPage;
